import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { CheckCircle, Loader2, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { withErrorCode } from '@/lib/errors'
import { useOrderStatus } from '../hooks/useOrderStatus'

const TIMEOUT_MS = 2 * 60 * 1000 // 2 minutes

export function OrderSuccessPage() {
  const [searchParams] = useSearchParams()
  const orderId = searchParams.get('orderId')
  const receiptToken = searchParams.get('receiptToken')
  const { order, loading, error } = useOrderStatus(orderId, receiptToken)
  const [timedOut, setTimedOut] = useState(false)

  // Start timeout once the order is confirmed received (not still loading)
  useEffect(() => {
    if (loading || order?.status !== 'pending_payment') return
    const timer = setTimeout(() => setTimedOut(true), TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [loading, order?.status])

  if (!orderId) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <p className="text-muted-foreground">
          {withErrorCode('We could not find that order.', 'shop/order-not-found')}
        </p>
        <Link to="/shop" className="mt-4 inline-block text-sm hover:underline">
          ← Back to shop
        </Link>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <Loader2 className="mx-auto h-10 w-10 animate-spin text-muted-foreground" />
        <p className="mt-4 text-muted-foreground">Loading your order…</p>
      </div>
    )
  }

  if (error || !order) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <p className="text-muted-foreground">This receipt link is missing or has expired.</p>
        <Button asChild className="mt-6">
          <Link to="/shop">Continue Shopping</Link>
        </Button>
      </div>
    )
  }

  const status = order?.status

  if (status === 'payment_failed') {
    return (
      <div className="container mx-auto max-w-lg px-4 py-20 text-center">
        <XCircle className="mx-auto h-14 w-14 text-destructive" />
        <h1 className="mt-4 text-2xl font-bold">Payment couldn&apos;t be completed</h1>
        <p className="mt-2 text-muted-foreground">
          {withErrorCode(
            'Your payment could not be processed. No charge was made.',
            'payments/order-failed',
          )}
        </p>
        <Button asChild className="mt-6">
          <Link to="/shop/checkout">Try Again</Link>
        </Button>
      </div>
    )
  }

  if (status === 'pending_payment') {
    if (timedOut) {
      return (
        <div className="container mx-auto max-w-lg px-4 py-20 text-center">
          <CheckCircle className="mx-auto h-14 w-14 text-green-500" />
          <h1 className="mt-4 text-2xl font-bold">Payment Received</h1>
          <p className="mt-2 text-muted-foreground">
            Your payment went through. We're processing your order and you'll receive a
            shipping confirmation by email once it's in production.
          </p>
          <Button asChild className="mt-6">
            <Link to="/shop">Continue Shopping</Link>
          </Button>
        </div>
      )
    }

    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <Loader2 className="mx-auto h-10 w-10 animate-spin text-muted-foreground" />
        <p className="mt-4 text-muted-foreground">Confirming your order…</p>
      </div>
    )
  }

  // submitted, in_production, shipped, or any other confirmed status
  return (
    <div className="container mx-auto max-w-lg px-4 py-20 text-center">
      <CheckCircle className="mx-auto h-14 w-14 text-green-500" />
      <h1 className="mt-4 text-2xl font-bold">Order Confirmed!</h1>
      <p className="mt-2 text-muted-foreground">
        Your order has been submitted and is heading to production. You'll receive shipping
        updates from Printify via email.
      </p>
      <Button asChild className="mt-6">
        <Link to="/shop">Continue Shopping</Link>
      </Button>
    </div>
  )
}
